import { useState } from 'react';
import {
  Box, Grid, Stack, Typography, Alert, Button, Chip, Card, TextField,
  MenuItem, FormControlLabel, Checkbox, Divider, LinearProgress,
  Select, InputLabel, FormControl, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { updateKycDoc, setKycCompanyType, setKycUserModified, addKycAdditionalDoc, updateKycAdditionalDoc, removeKycAdditionalDoc } from '../../../../redux/pricingWizardSlice';
import { KYC_DOC_DEFS, IFSC_BANKS } from '../constants';
import Masonry from '@mui/lab/Masonry';

const STATUS = {
  not_uploaded: { label: 'Not uploaded', chip: 'default',  accent: '#e2e8f0', icon: null },
  uploaded:     { label: 'Uploaded',      chip: 'primary', accent: '#6366f1', icon: CheckCircleIcon },
  under_review: { label: 'In review',     chip: 'warning', accent: '#f59e0b', icon: HourglassEmptyIcon },
  approved:     { label: 'Approved',      chip: 'success', accent: '#22c55e', icon: TaskAltIcon },
  rejected:     { label: 'Rejected',      chip: 'error',   accent: '#ef4444', icon: CancelIcon },
  not_required: { label: 'Not required',  chip: 'default', accent: '#e2e8f0', icon: null },
};

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600, minWidth: 90 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary', fontFamily: 'monospace' }}>{value}</Typography>
    </Stack>
  );
}

function DocCard({ def, doc, locked, onFileCapture }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const set = (patch) => {
    dispatch(updateKycDoc({ docId: def.key, patch }));
    dispatch(setKycUserModified(true));
  };
  const status = doc.status || 'not_uploaded';
  const s = STATUS[status] || STATUS.not_uploaded;
  const StatusIcon = s.icon;
  // per-card edit toggle: when doc has a presigned URL from API, show read-only until Edit clicked
  const [editMode, setEditMode] = useState(false);
  const hasExistingDoc = !!(doc.docUrl || doc.presignedUrl);
  const showForm = editMode || !hasExistingDoc;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    set({ file: { name: f.name, size: f.size, type: f.type }, status: 'uploaded' });
    if (onFileCapture) onFileCapture(def.key, f);
    dispatch(setKycUserModified(true));
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'divider',
        overflow: 'hidden',
        transition: 'box-shadow .2s',
        '&:hover': { boxShadow: '0 2px 12px rgba(15,23,42,0.08)' },
      }}
    >
      {/* coloured accent bar */}
      <Box sx={{ height: 3, bgcolor: s.accent, transition: 'background-color .2s' }} />

      {/* header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.25 }}>
            {def.label}
          </Typography>
        </Box>
        {hasExistingDoc && !locked && status !== 'approved' && (
          <Button size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 13 }} />}
            onClick={() => setEditMode((v) => !v)}
            sx={{ fontSize: 11.5, fontWeight: 600, minWidth: 0, px: 1 }}>
            {editMode ? 'Cancel' : 'Edit'}
          </Button>
        )}
        <Chip
          size="small"
          color={s.chip}
          variant={status === 'not_uploaded' || status === 'not_required' ? 'outlined' : 'soft'}
          icon={StatusIcon ? <StatusIcon sx={{ fontSize: '13px !important' }} /> : undefined}
          label={s.label}
          sx={{ fontWeight: 700, fontSize: 10.5, height: 22, '& .MuiChip-icon': { ml: '6px' } }}
        />
      </Stack>

      <Divider />

      {/* body */}
      <Box sx={{ px: 2, py: 1.5 }}>
        {/* Uploaded document read-only view */}
        {hasExistingDoc && !showForm && (
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'success.dark', flex: 1 }}>
                Document uploaded{doc.uploadDate ? ` · ${new Date(doc.uploadDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}` : ''}
              </Typography>
              {(doc.docUrl || doc.presignedUrl) && (
                <Button size="small" variant="outlined" endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                  onClick={() => window.open(doc.docUrl || doc.presignedUrl, '_blank')}
                  sx={{ fontSize: 11.5, fontWeight: 600, borderRadius: '8px', flexShrink: 0 }}>
                  View
                </Button>
              )}
            </Stack>

            {/* Per-doc text details */}
            <Stack spacing={0.5} sx={{ pl: 0.5 }}>
              {def.key === 'msme' && doc.not_registered && (
                <InfoRow label="Registration" value="Not registered under MSME / Udyam" />
              )}
              {def.hasNumber && doc.number && !doc.not_registered && (
                <InfoRow label={def.numLabel} value={doc.number} />
              )}
              {def.bankProof && (
                <>
                  {doc.beneficiary_name && <InfoRow label="Beneficiary" value={doc.beneficiary_name} />}
                  {doc.account_no && <InfoRow label="Account No." value={doc.account_no} />}
                  {doc.ifsc && <InfoRow label="IFSC" value={doc.ifsc} />}
                  {doc.bank_name && <InfoRow label="Bank" value={doc.bank_name} />}
                  {doc.account_type && <InfoRow label="Account Type" value={doc.account_type} />}
                </>
              )}
              {def.signatory && (
                <>
                  {doc.name && <InfoRow label="Name" value={doc.name} />}
                  {doc.dob && <InfoRow label="DOB" value={doc.dob} />}
                  {doc.aadhaar_no && <InfoRow label="Aadhaar No." value={doc.aadhaar_no} />}
                </>
              )}
            </Stack>
          </Box>
        )}

        {showForm && (<Grid container spacing={1.25}>
            {def.key === 'msme' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox size="small" checked={!!doc.not_registered} disabled={locked}
                      onChange={(e) => set(e.target.checked
                        ? { not_registered: true, status: 'not_required', file: null }
                        : { not_registered: false, status: 'not_uploaded' })} />
                  }
                  label={<Typography variant="caption" sx={{ fontWeight: 500 }}>Not registered under MSME / Udyam</Typography>}
                />
              </Grid>
            )}
            {def.hasNumber && !doc.not_registered && (
              <Grid item xs={12}>
                <TextField size="small" fullWidth label={def.numLabel} placeholder={def.numPlaceholder}
                  value={doc.number || ''} disabled={locked} onChange={(e) => set({ number: e.target.value })} />
              </Grid>
            )}
            {def.bankProof && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth label="Account Number" value={doc.account_no || ''} disabled={locked}
                    onChange={(e) => set({ account_no: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth label="IFSC" placeholder="HDFC0001234" value={doc.ifsc || ''} disabled={locked}
                    onChange={(e) => { const ifsc = e.target.value.toUpperCase(); set({ ifsc, bank_name: IFSC_BANKS[ifsc.slice(0, 4)] || '' }); }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField size="small" fullWidth label="Bank Name" placeholder="e.g. HDFC Bank"
                    value={doc.bank_name || ''} disabled={locked}
                    onChange={(e) => set({ bank_name: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth label="Beneficiary Name" placeholder="Account holder name"
                    value={doc.beneficiary_name || ''} disabled={locked}
                    onChange={(e) => set({ beneficiary_name: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField size="small" fullWidth select label="Account Type"
                    value={doc.account_type || 'CURRENT'} disabled={locked}
                    onChange={(e) => set({ account_type: e.target.value })}>
                    <MenuItem value="CURRENT">Current Account</MenuItem>
                    <MenuItem value="SAVING">Savings Account</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}
            {def.signatory && (
              <>
                <Grid item xs={12} md={6}><TextField size="small" fullWidth label="Name" value={doc.name || ''} disabled={locked} onChange={(e) => set({ name: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField size="small" fullWidth type="date" label="DOB" InputLabelProps={{ shrink: true }} value={doc.dob || ''} disabled={locked} onChange={(e) => set({ dob: e.target.value })} /></Grid>
                <Grid item xs={12} md={3}><TextField size="small" fullWidth label="Aadhaar Number" inputProps={{ maxLength: 12 }} placeholder="234500001234" value={doc.aadhaar_no || ''} disabled={locked} onChange={(e) => set({ aadhaar_no: e.target.value })} /></Grid>
              </>
            )}
            {def.addressProof && (
              <Grid item xs={12}>
                <TextField size="small" fullWidth select label="Proof type" value={doc.type || 'utility'} disabled={locked} onChange={(e) => set({ type: e.target.value })}>
                  <MenuItem value="utility">Utility bill (last 3 months)</MenuItem>
                  <MenuItem value="rent">Rent agreement</MenuItem>
                  <MenuItem value="property">Property tax receipt</MenuItem>
                </TextField>
              </Grid>
            )}

            {/* upload row — hidden for MSME when not_registered */}
            {!(def.key === 'msme' && doc.not_registered) && <Grid item xs={12}>
              {doc.file ? (
                <Stack direction="row" alignItems="center" spacing={0.5}
                  sx={{ px: 1.5, py: 0.75, border: 1, borderColor: 'success.light', borderRadius: '8px', bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'success.dark', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.file.name}
                  </Typography>
                  {!locked && (
                    <IconButton size="small" onClick={(e) => {
                      e.preventDefault();
                      set({ file: null, status: 'not_uploaded' });
                      if (onFileCapture) onFileCapture(def.key, null);
                    }}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08) }, flexShrink: 0 }}>
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  )}
                </Stack>
              ) : (
                <Button component="label" size="small" variant="outlined" color="primary"
                  startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 15 }} />}
                  disabled={locked} disableElevation
                  sx={{ fontWeight: 600, fontSize: 12, borderRadius: '8px' }}>
                  Upload file
                  <input hidden type="file" accept="application/pdf,image/*" onChange={onFile} />
                </Button>
              )}
            </Grid>}
          </Grid>)}


        {status === 'rejected' && (
          <Alert severity="error" sx={{ mt: 1.25, py: 0.4, fontSize: 12 }}>
            <b>Rejected</b> — {doc.rejection_reason || 'document unclear'}. Re-upload to retry.
          </Alert>
        )}
      </Box>
    </Card>
  );
}

export default function Stage1_Kyc({ onFileCapture }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const kyc = useSelector((s) => s.pricingWizard.kyc);
  const unlocks = useSelector((s) => s.pricingWizard.unlocks);
  const locked = Boolean(kyc.submittedAt) && !unlocks?.[1];
  const companyType = useSelector((s) => s.pricingWizard.kyc.companyType);
  const additionalDocs = useSelector((s) => s.pricingWizard.kyc.additionalDocs || []);
  const onAdditionalFile = (id, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    dispatch(updateKycAdditionalDoc({ id, patch: { file: { name: f.name, size: f.size, type: f.type } } }));
    if (onFileCapture) onFileCapture(`additional:${id}`, f);
    dispatch(setKycUserModified(true));
  };

  const uploadedCount = KYC_DOC_DEFS.filter((d) => {
    const doc = kyc.docs[d.key];
    if (d.key === 'msme' && doc.not_registered) return true;
    return doc.status && doc.status !== 'not_uploaded';
  }).length;
  const progress = Math.round((uploadedCount / KYC_DOC_DEFS.length) * 100);

  // Conditional mandatory: if any field is touched, these 4 become required
  const hasAnyData = !!(
    companyType ||
    kyc.docs.pan.number || kyc.docs.pan.file ||
    kyc.docs.gstin.number || kyc.docs.gstin.file ||
    kyc.docs.bank.account_no || kyc.docs.bank.file ||
    kyc.docs.signatory.file || kyc.docs.signatory.aadhaar_no || kyc.docs.signatory.name ||
    kyc.docs.msme.number || kyc.docs.msme.file
  );
  const missingMandatory = hasAnyData && !locked ? [
    !companyType                        && 'Company Type',
    !(kyc.docs.bank.file || (kyc.docs.bank.account_no && kyc.docs.bank.ifsc)) && 'Bank Details',
    !(kyc.docs.gstin.file || kyc.docs.gstin.number)   && 'GST Details',
    !(kyc.docs.signatory.file || kyc.docs.signatory.aadhaar_no) && 'Authorised Signatory KYC',
  ].filter(Boolean) : [];

  return (
    <Box>
      {/* progress strip */}
      <Box sx={{ mb: 2.5, p: 2, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: locked ? alpha(theme.palette.success.main, 0.04) : alpha(theme.palette.grey[500], 0.03) }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {locked
              ? `KYC submitted on ${new Date(kyc.submittedAt).toLocaleString()}`
              : `${uploadedCount} of ${KYC_DOC_DEFS.length} documents uploaded`}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: uploadedCount > 0 ? 'success.main' : 'text.secondary' }}>
            {progress}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 5, borderRadius: 99, bgcolor: 'grey.100',
            '& .MuiLinearProgress-bar': { bgcolor: locked ? 'success.main' : uploadedCount > 0 ? 'success.main' : 'primary.main', borderRadius: 99 },
          }}
        />
        {!locked && (
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.75 }}>
            KYC runs in parallel — you can navigate other steps freely. Only the final Commit is gated by KYC approval.
          </Typography>
        )}
      </Box>

      {/* Mandatory fields warning — shown when any data is entered */}
      {missingMandatory.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          <b>Required to continue:</b> {missingMandatory.join(', ')} must be completed since you have started filling KYC details.
        </Alert>
      )}

      {/* company type — required for API 2 */}
      {!locked && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Company Type *</InputLabel>
            <Select
              label="Company Type *"
              value={companyType}
              onChange={(e) => { dispatch(setKycCompanyType(e.target.value)); dispatch(setKycUserModified(true)); }}
            >
              <MenuItem value="Limited Company">Private Limited Company</MenuItem>
              <MenuItem value="Limited Liability Partnership">Limited Liability Partnership</MenuItem>
              <MenuItem value="Partnership Firm">Partnership Firm</MenuItem>
              <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* document grid — masonry packs variable-height cards so there are no ragged gaps */}
      <Masonry columns={{ xs: 1, md: 2 }} spacing={1.75} sx={{ m: 0 }}>
        {KYC_DOC_DEFS.map((def) => (
          <Box key={def.key}>
            <DocCard def={def} doc={kyc.docs[def.key]}
              locked={locked && kyc.docs[def.key].status !== 'under_review' && kyc.docs[def.key].status !== 'rejected'}
              onFileCapture={onFileCapture} />
          </Box>
        ))}
      </Masonry>



      {/* Additional documents — free-text type + upload + remark */}
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <NoteAddOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Additional Documents</Typography>
          </Stack>
          <Button size="small" variant="outlined" startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => dispatch(addKycAdditionalDoc())} sx={{ borderRadius: '8px', fontWeight: 600 }}>
            Add document
          </Button>
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.25 }}>
          Add any extra document type required for this merchant — name it, upload the file, and add a remark.
        </Typography>

        {additionalDocs.length === 0 ? (
          <Box sx={{ p: 2, border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No additional documents added.</Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {additionalDocs.map((d) => (
              <Card key={d.id} variant="outlined" sx={{ borderRadius: 2, p: 1.75 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField size="small" fullWidth label="Document Type" placeholder="e.g. NOC, Trademark cert."
                      value={d.type} disabled={locked}
                      onChange={(e) => dispatch(updateKycAdditionalDoc({ id: d.id, patch: { type: e.target.value } }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField size="small" fullWidth label="Remark" placeholder="Optional note"
                      value={d.remark} disabled={locked}
                      onChange={(e) => dispatch(updateKycAdditionalDoc({ id: d.id, patch: { remark: e.target.value } }))} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {d.file ? (
                      <Stack direction="row" alignItems="center" spacing={0.5}
                        sx={{ px: 1, py: 0.75, border: 1, borderColor: 'success.light', borderRadius: '8px', bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'success.dark', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.file.name}
                        </Typography>
                      </Stack>
                    ) : (
                      <Button component="label" size="small" variant="outlined" disabled={locked}
                        startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 15 }} />}
                        sx={{ fontWeight: 600, fontSize: 12, borderRadius: '8px' }}>
                        Upload file
                        <input hidden type="file" accept="application/pdf,image/*" onChange={(e) => onAdditionalFile(d.id, e)} />
                      </Button>
                    )}
                  </Grid>
                  <Grid item xs={12} md={1} sx={{ textAlign: 'right' }}>
                    <IconButton size="small" color="error" disabled={locked}
                      onClick={() => dispatch(removeKycAdditionalDoc(d.id))}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Stack>
        )}
      </Box>

      {kyc.overallStatus === 'approved' && (
        <Alert severity="success" icon={<TaskAltIcon fontSize="small" />} sx={{ mt: 2.5 }}>
          <b>KYC Approved</b> — the Commit step is now unlocked.
        </Alert>
      )}
    </Box>
  );
}
